import { Flex, Tag, Text } from '@spotify-internal/encore-web';
import { extractLastNameComponent } from '@spotify/confidence-core-react';
import {
  MetricsV1MetricMetricVerificationVerificationState,
  MetricsV1MetricPreferredDirection,
  getTypeOrNull,
  useMetricQuery,
} from '@spotify/confidence-graphql';
import {
  getMeasurementConfigKindDescription,
  getMetricTypeDisplayLabel,
} from '@spotify/plugin-confidence-metrics-react';
import { useSetResolvedTitle } from '@spotify/plugin-confidence-projects-react';

import {
  KeyValueRow,
  PreviewShell,
  SummarySection,
  TypeStatusRow,
  resolveDisplayName,
} from './components';

interface MetricArtifactPreviewProps {
  resourceName: string;
  label: string;
}

const formatDirection = (
  direction?: MetricsV1MetricPreferredDirection | null,
): string | undefined => {
  if (direction === MetricsV1MetricPreferredDirection.Increase)
    return 'Increase';
  if (direction === MetricsV1MetricPreferredDirection.Decrease)
    return 'Decrease';
  return undefined;
};

const formatVerification = (
  state?: MetricsV1MetricMetricVerificationVerificationState | null,
): string => {
  if (state === MetricsV1MetricMetricVerificationVerificationState.Verified) {
    return 'Verified';
  }
  return 'Unverified';
};

export const MetricArtifactPreview = ({
  resourceName,
  label,
}: MetricArtifactPreviewProps) => {
  const { data, loading, error } = useMetricQuery({
    variables: { name: resourceName },
  });

  const metric = getTypeOrNull(data?.metric);
  const entity = getTypeOrNull(metric?.entity);
  const factTable = getTypeOrNull(metric?.factTable);
  const previewError = error ?? (!loading && !metric ? 'not-found' : undefined);

  useSetResolvedTitle(
    metric?.displayName || extractLastNameComponent(resourceName),
  );

  const metricType = metric ? getMetricTypeDisplayLabel(metric) : undefined;
  const direction = formatDirection(metric?.preferredDirection);
  const timeWindow = metric?.measurementConfig
    ? getMeasurementConfigKindDescription(metric.measurementConfig)
    : undefined;
  const varianceReduction = metric?.varianceReductionConfig?.disabled
    ? 'Disabled'
    : 'Enabled';
  const verification = metric?.verification?.state;

  return (
    <PreviewShell loading={loading} error={previewError}>
      {metric && (
        <>
          <TypeStatusRow
            typeLabel={label}
            state={metric.state ?? undefined}
            demoLabels={metric.labels}
          />
          {metric.description && (
            <Text variant="bodySmall" semanticColor="textSubdued">
              {metric.description}
            </Text>
          )}
          <SummarySection title="Configuration">
            <Flex direction="column" gap="tighter-2">
              {metricType && (
                <KeyValueRow label="Type">
                  <Tag>{metricType}</Tag>
                </KeyValueRow>
              )}
              {entity && (
                <KeyValueRow label="Entity">
                  <Tag>{resolveDisplayName(entity)}</Tag>
                </KeyValueRow>
              )}
              {factTable && (
                <KeyValueRow label="Fact table">
                  <Tag>{resolveDisplayName(factTable)}</Tag>
                </KeyValueRow>
              )}
              {direction && (
                <KeyValueRow label="Direction">
                  <Tag>{direction}</Tag>
                </KeyValueRow>
              )}
            </Flex>
          </SummarySection>
          {timeWindow && (
            <SummarySection title="Time window">
              <KeyValueRow label="Measure">
                <Tag>{timeWindow}</Tag>
              </KeyValueRow>
            </SummarySection>
          )}
          <SummarySection title="Status">
            <Flex direction="column" gap="tighter-2">
              <KeyValueRow label="Verification">
                <Tag
                  colorSet={
                    verification ===
                    MetricsV1MetricMetricVerificationVerificationState.Verified
                      ? 'positiveSubdued'
                      : undefined
                  }
                >
                  {formatVerification(verification)}
                </Tag>
              </KeyValueRow>
              <KeyValueRow label="Variance reduction">
                <Tag>{varianceReduction}</Tag>
              </KeyValueRow>
            </Flex>
          </SummarySection>
        </>
      )}
    </PreviewShell>
  );
};
